import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Typography from "@material-ui/core/Typography";
import { Badge, Tooltip } from "@material-ui/core";
import InboxOutlinedIcon from "@material-ui/icons/InboxOutlined";
import { useInbox } from "../context/Inbox";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import QuestionAnswerOutlinedIcon from "@material-ui/icons/QuestionAnswerOutlined";
import BusinessOutlinedIcon from "@material-ui/icons/BusinessOutlined";
import PhoneIphoneOutlinedIcon from "@material-ui/icons/PhoneIphoneOutlined";
import HeadsetMicOutlinedIcon from "@material-ui/icons/HeadsetMicOutlined";
import NewReleasesOutlinedIcon from "@material-ui/icons/NewReleasesOutlined";
import AccountBalanceWalletOutlinedIcon from "@material-ui/icons/AccountBalanceWalletOutlined";
import DesktopWindowsOutlinedIcon from "@material-ui/icons/DesktopWindowsOutlined";

import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";

const useStyles = makeStyles((theme) => ({
	navItem: {
		borderRadius: 10,
		marginBottom: 2,
		padding: "8px 12px",
		color: theme.custom.sidebarText,
		"&:hover": {
			backgroundColor: "rgba(148, 163, 184, 0.1)",
		},
		"& .MuiListItemIcon-root": {
			color: "inherit",
			minWidth: 38,
		},
		"& .MuiListItemText-primary": {
			fontSize: "0.875rem",
			fontWeight: 500,
		},
	},
	navItemActive: {
		backgroundColor: theme.custom.sidebarActiveBg,
		color: theme.custom.sidebarActiveText,
		"&:hover": {
			backgroundColor: theme.custom.sidebarActiveBg,
		},
		"& .MuiListItemText-primary": {
			fontWeight: 700,
		},
	},
	navItemCollapsed: {
		justifyContent: "center",
		padding: "10px 0",
		"& .MuiListItemIcon-root": {
			minWidth: "auto",
			justifyContent: "center",
		},
	},
	sectionLabel: {
		display: "block",
		padding: "16px 12px 6px",
		fontSize: "0.65rem",
		fontWeight: 700,
		textTransform: "uppercase",
		letterSpacing: "0.1em",
		color: "rgba(148, 163, 184, 0.6)",
		whiteSpace: "nowrap",
		overflow: "hidden",
	},
	"@keyframes tutorialGlow": {
		"0%": { boxShadow: "0 0 0 0 rgba(16,185,129,.5)" },
		"70%": { boxShadow: "0 0 0 10px rgba(16,185,129,0)" },
		"100%": { boxShadow: "0 0 0 0 rgba(16,185,129,0)" },
	},
	tutorialItem: {
		borderRadius: 10,
		marginTop: 10,
		marginBottom: 2,
		padding: "10px 12px",
		background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
		color: "#fff",
		boxShadow: "0 4px 14px rgba(16,185,129,.4)",
		animation: "$tutorialGlow 2.4s ease-out infinite",
		"&:hover": {
			background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
		},
		"& .MuiListItemIcon-root": {
			color: "#fff",
			minWidth: 38,
		},
		"& .MuiListItemText-primary": {
			fontSize: "0.875rem",
			fontWeight: 800,
		},
	},
}));

function ListItemLink(props) {
	const { icon, primary, to, collapsed, tour } = props;
	const classes = useStyles();
	const location = useLocation();

	const isActive =
		to === "/"
			? location.pathname === "/"
			: location.pathname.toLowerCase().startsWith(to.toLowerCase());

	const renderLink = React.useMemo(
		() =>
			React.forwardRef(function SidebarLink(itemProps, ref) {
				return <RouterLink to={to} ref={ref} {...itemProps} />;
			}),
		[to]
	);

	const item = (
		<ListItem
			button
			component={renderLink}
			data-tour={tour}
			className={`${classes.navItem} ${
				isActive ? classes.navItemActive : ""
			} ${collapsed ? classes.navItemCollapsed : ""}`}
		>
			{icon ? <ListItemIcon>{icon}</ListItemIcon> : null}
			{!collapsed && <ListItemText primary={primary} />}
		</ListItem>
	);

	return (
		<li>
			{collapsed ? (
				<Tooltip title={primary} placement="right" arrow>
					{item}
				</Tooltip>
			) : (
				item
			)}
		</li>
	);
}

// O Financeiro e um app separado servido em /financeiro/, fora do react-router
// desta SPA — por isso ancora normal em vez de RouterLink.
function ListItemExternal(props) {
	const { icon, primary, href, collapsed, tour } = props;
	const classes = useStyles();

	const item = (
		<ListItem
			button
			component="a"
			href={href}
			target="_blank"
			rel="noopener noreferrer"
			data-tour={tour}
			className={`${classes.navItem} ${
				collapsed ? classes.navItemCollapsed : ""
			}`}
		>
			{icon ? <ListItemIcon>{icon}</ListItemIcon> : null}
			{!collapsed && <ListItemText primary={primary} />}
		</ListItem>
	);

	return (
		<li>
			{collapsed ? (
				<Tooltip title={primary} placement="right" arrow>
					{item}
				</Tooltip>
			) : (
				item
			)}
		</li>
	);
}

const MainListItems = (props) => {
	const { drawerClose, collapsed } = props;
	const classes = useStyles();
	const { whatsApps } = useContext(WhatsAppsContext);
	const { user } = useContext(AuthContext);
	const { unreadCount } = useInbox();
	const [connectionWarning, setConnectionWarning] = useState(false);

	useEffect(() => {
		const delayDebounceFn = setTimeout(() => {
			if (whatsApps.length > 0) {
				const offlineWhats = whatsApps.filter((whats) => {
					return (
						whats.status === "qrcode" ||
						whats.status === "PAIRING" ||
						whats.status === "DISCONNECTED" ||
						whats.status === "TIMEOUT" ||
						whats.status === "OPENING"
					);
				});
				if (offlineWhats.length > 0) {
					setConnectionWarning(true);
				} else {
					setConnectionWarning(false);
				}
			}
		}, 2000);
		return () => clearTimeout(delayDebounceFn);
	}, [whatsApps]);

	return (
		<div onClick={drawerClose}>
			{!collapsed && (
				<Typography component="span" className={classes.sectionLabel}>
					Principal
				</Typography>
			)}
			<ListItemLink
				collapsed={collapsed}
				to="/"
				primary="Dashboard"
				icon={<DashboardOutlinedIcon />}
				tour="menu-dashboard"
			/>
			<ListItemLink
				collapsed={collapsed}
				to="/tickets"
				primary={i18n.t("mainDrawer.listItems.tickets")}
				icon={<WhatsAppIcon />}
				tour="menu-tickets"
			/>
			<ListItemLink collapsed={collapsed} to="/inbox" primary="Meu inbox" icon={<Badge badgeContent={unreadCount} max={99} color="primary"><InboxOutlinedIcon /></Badge>} />
			<ListItemLink
				collapsed={collapsed}
				to="/contacts"
				primary={i18n.t("mainDrawer.listItems.contacts")}
				icon={<ContactPhoneOutlinedIcon />}
				tour="menu-contacts"
			/>
			<ListItemLink
				collapsed={collapsed}
				to="/quickAnswers"
				primary={i18n.t("mainDrawer.listItems.quickAnswers")}
				icon={<QuestionAnswerOutlinedIcon />}
				tour="menu-quickanswers"
			/>
			<ListItemLink
				collapsed={collapsed}
				to="/connections"
				tour="menu-connections"
				primary={i18n.t("mainDrawer.listItems.connections")}
				icon={
					<Badge
						badgeContent={connectionWarning ? "!" : 0}
						color="error"
						overlap="rectangular"
					>
						<SyncAltIcon />
					</Badge>
				}
			/>
			{/* O super admin sempre ve: o painel dele e cross-empresa, e a
			    empresa dele (a padrao) normalmente nao contrata o modulo — sem
			    esta excecao ele fica trancado fora do proprio painel. */}
			{(user?.super || user?.modules?.includes("flydesk")) && (
				<ListItemLink
					collapsed={collapsed}
					to="/flydesk/devices"
					primary={i18n.t("mainDrawer.listItems.flydesk")}
					icon={<DesktopWindowsOutlinedIcon />}
					tour="menu-flydesk"
				/>
			)}
			{user?.modules?.includes("financeiro") && (
				<ListItemExternal
					collapsed={collapsed}
					href="/financeiro/"
					primary={i18n.t("mainDrawer.listItems.financeiro")}
					icon={<AccountBalanceWalletOutlinedIcon />}
					tour="menu-financeiro"
				/>
			)}
			<li>
				{(() => {
					const item = (
						<ListItem
							button
							data-tour="menu-tutorial"
							className={`${classes.tutorialItem} ${
								collapsed ? classes.navItemCollapsed : ""
							}`}
							onClick={() =>
								window.dispatchEvent(new Event("flytec:start-tutorial"))
							}
						>
							<ListItemIcon>
								<HeadsetMicOutlinedIcon />
							</ListItemIcon>
							{!collapsed && <ListItemText primary="Tutorial" />}
						</ListItem>
					);
					return collapsed ? (
						<Tooltip title="Tutorial guiado por voz" placement="right" arrow>
							{item}
						</Tooltip>
					) : (
						item
					);
				})()}
			</li>
			<ListItemLink
				collapsed={collapsed}
				to="/updates"
				primary="Atualizações"
				icon={<NewReleasesOutlinedIcon />}
			/>
			<Can
				role={user.profile}
				perform="drawer-admin-items:view"
				yes={() => (
					<>
						{!collapsed && (
							<Typography component="span" className={classes.sectionLabel}>
								{i18n.t("mainDrawer.listItems.administration")}
							</Typography>
						)}
						<ListItemLink
							collapsed={collapsed}
							to="/users"
							primary={i18n.t("mainDrawer.listItems.users")}
							icon={<PeopleAltOutlinedIcon />}
							tour="menu-users"
						/>
						<ListItemLink
							collapsed={collapsed}
							to="/queues"
							primary={i18n.t("mainDrawer.listItems.queues")}
							icon={<AccountTreeOutlinedIcon />}
							tour="menu-queues"
						/>
						<ListItemLink
							collapsed={collapsed}
							to="/test-mode"
							primary={i18n.t("mainDrawer.listItems.testMode")}
							icon={<PhoneIphoneOutlinedIcon />}
						/>
						<ListItemLink
							collapsed={collapsed}
							to="/settings"
							primary={i18n.t("mainDrawer.listItems.settings")}
							icon={<SettingsOutlinedIcon />}
							tour="menu-settings"
						/>
					</>
				)}
			/>
			{user.super && (
				<>
					{!collapsed && (
						<Typography component="span" className={classes.sectionLabel}>
							{i18n.t("mainDrawer.listItems.superSection")}
						</Typography>
					)}
					<ListItemLink
						collapsed={collapsed}
						to="/companies"
						primary={i18n.t("mainDrawer.listItems.companies")}
						icon={<BusinessOutlinedIcon />}
					/>
				</>
			)}
		</div>
	);
};

export default MainListItems;
